import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { announcementsAPI, API_BASE_URL } from '../services/api';
import { usePreview } from '../contexts/PreviewContext';
import PDFPreviewModal from './PDFPreviewModal';
import VideoEmbed, { parseVideoUrl } from './VideoEmbed';
import ModalShell from './ModalShell';

const Announcements = () => {
  const { previewDomain, isPreviewMode } = usePreview();

  // Download handler for internal API URLs (needs auth headers)
  const handleDownload = async (url, filename) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}${url}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download file');
    }
  };
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openPdfModals, setOpenPdfModals] = useState([]); // [{id, url, title}]
  const [openVideoModals, setOpenVideoModals] = useState([]); // [{id, type, videoId, title}]
  const [openNativeVideoModals, setOpenNativeVideoModals] = useState([]); // [{id, url, title}]
  const modalIdRef = React.useRef(0);
  const nextModalId = () => ++modalIdRef.current;

  useEffect(() => {
    fetchAnnouncements();
  }, [isPreviewMode, previewDomain]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      let data;
      if (isPreviewMode && previewDomain) {
        // Fetch preview data
        const result = await announcementsAPI.previewAsClient(previewDomain);
        data = result.announcements || [];
      } else {
        // Normal fetch
        data = await announcementsAPI.getAnnouncements(true, 10);
      }
      setAnnouncements(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
      setError('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffTime / (1000 * 60));
        return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
      }
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    }
  };

  const getCategoryBadge = (category) => {
    const categoryStyles = {
      general: { bg: 'bg-secondary', label: 'General' },
      update: { bg: 'bg-info', label: 'Update' },
      alert: { bg: 'bg-warning text-dark', label: 'Alert' },
      performance: { bg: 'bg-success', label: 'Performance' },
    };
    const key = (category || 'general').toLowerCase();
    const style = categoryStyles[key] || { bg: 'bg-secondary', label: category || 'General' };
    return (
      <span className={`badge ${style.bg} me-2`} style={{ fontSize: '0.7rem' }}>
        {style.label}
      </span>
    );
  };

  // Render content with embedded images and videos
  const renderContent = (content) => {
    // Combined regex for images ![alt](url) and videos [video](url)
    const mediaRegex = /(?:!\[([^\]]*)\]\(([^)]+)\)|\[video\]\(([^)]+)\))/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = mediaRegex.exec(content)) !== null) {
      // Add text before the media
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: content.substring(lastIndex, match.index),
        });
      }

      // Check if it's an image or video
      if (match[2]) {
        // Image: ![alt](url)
        const imageUrl = match[2].startsWith('/')
          ? `${API_BASE_URL}${match[2]}`
          : match[2];
        parts.push({
          type: 'image',
          alt: match[1] || 'image',
          url: imageUrl,
        });
      } else if (match[3]) {
        // Video: [video](url)
        const videoUrl = match[3].startsWith('/')
          ? `${API_BASE_URL}${match[3]}`
          : match[3];
        parts.push({
          type: 'video',
          url: videoUrl,
        });
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last media
    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.substring(lastIndex),
      });
    }

    // If no media found, return text as-is
    if (parts.length === 0) {
      return content;
    }

    return (
      <>
        {parts.map((part, index) => {
          if (part.type === 'image') {
            return (
              <img
                key={index}
                src={part.url}
                alt={part.alt}
                style={{
                  maxWidth: '100%',
                  borderRadius: '4px',
                  marginTop: '0.5rem',
                  marginBottom: '0.5rem',
                }}
              />
            );
          } else if (part.type === 'video') {
            return (
              <video
                key={index}
                controls
                style={{
                  maxWidth: '100%',
                  borderRadius: '4px',
                  marginTop: '0.5rem',
                  marginBottom: '0.5rem',
                  backgroundColor: '#000',
                }}
              >
                <source src={part.url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            );
          } else {
            return (
              <span key={index} style={{ whiteSpace: 'pre-wrap' }}>
                {part.content}
              </span>
            );
          }
        })}
      </>
    );
  };

  if (loading) {
    return (
      <div className="announcements-premium">
        <div className="card-header">
          <span className="announcements-icon">📢</span>
          <span className="announcements-title">Announcements</span>
        </div>
        <div className="announcements-body text-center py-4">
          <div className="spinner-border spinner-border-sm" role="status" style={{ color: 'var(--secondary-color)' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="announcements-premium">
        <div className="card-header">
          <span className="announcements-icon">📢</span>
          <span className="announcements-title">Announcements</span>
        </div>
        <div className="announcements-body p-4">
          <div className="alert alert-danger mb-0">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="announcements-premium">
      <div className="card-header">
        <span className="announcements-icon">📢</span>
        <span className="announcements-title">Announcements</span>
      </div>
      <div className="announcements-body">
        {announcements.length === 0 ? (
          <div className="announcements-empty">
            No announcements at this time
          </div>
        ) : (
          <div className="announcements-feed">
            {announcements.map((announcement, index) => (
              <div
                key={announcement.id}
                className={`announcement-item-premium ${announcement.is_pinned ? 'pinned' : ''}`}
              >
                {/* Header row with author and date */}
                <div className="announcement-author">
                  <div className="announcement-avatar">
                    {announcement.author_name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)}
                  </div>
                  <div className="announcement-author-info">
                    <div className="announcement-author-name">
                      {announcement.author_name}
                      {announcement.is_pinned && (
                        <span className="announcement-pinned" title="Pinned">📌</span>
                      )}
                    </div>
                    <div className="announcement-meta">
                      {formatDate(announcement.created_at)}
                    </div>
                  </div>
                </div>

                {/* Title and category */}
                <div className="announcement-header">
                  {getCategoryBadge(announcement.category)}
                  <span className="announcement-title">
                    {announcement.title}
                  </span>
                </div>

                {/* Content */}
                <div className="announcement-content">
                  {renderContent(announcement.content)}
                </div>

                {/* Document Attachment Link */}
                {announcement.document_url && (
                  <div className="mt-2 announcement-attachment">
                    {/* Show thumbnail if available */}
                    {announcement.preview_url && (
                      <img
                        src={announcement.preview_url.startsWith('http')
                          ? announcement.preview_url
                          : `${API_BASE_URL}${announcement.preview_url}`}
                        alt="Preview"
                        className="attachment-thumbnail"
                        onClick={() => {
                          if (announcement.preview_type === 'pdf') {
                            setOpenPdfModals(prev => [...prev, {
                              id: nextModalId(),
                              url: `${API_BASE_URL}${announcement.document_url}`,
                              title: announcement.document_name
                            }]);
                          }
                        }}
                        style={{
                          cursor: announcement.preview_type === 'pdf' ? 'pointer' : 'default',
                          maxWidth: '120px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          marginRight: '8px'
                        }}
                      />
                    )}
                    {announcement.preview_type === 'video' ? (
                      // Uploaded video - open in native video player modal
                      <button
                        onClick={() => setOpenNativeVideoModals(prev => [...prev, {
                          id: nextModalId(),
                          url: `${API_BASE_URL}${announcement.document_url}`,
                          title: announcement.document_name
                        }])}
                        className="btn btn-sm btn-outline-primary"
                        style={{ textDecoration: 'none' }}
                      >
                        🎬 {announcement.document_name || 'Watch Video'}
                      </button>
                    ) : announcement.preview_type === 'vimeo' ? (
                      // Vimeo - open in embed player modal
                      (() => {
                        const vimeoMatch = announcement.document_url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
                        const vimeoId = vimeoMatch ? vimeoMatch[1] : null;
                        return vimeoId ? (
                          <button
                            onClick={() => setOpenVideoModals(prev => [...prev, {
                              id: nextModalId(),
                              type: 'vimeo',
                              videoId: vimeoId,
                              title: announcement.document_name
                            }])}
                            className="btn btn-sm btn-outline-primary"
                            style={{ textDecoration: 'none' }}
                          >
                            🎬 {announcement.document_name || 'Watch on Vimeo'}
                          </button>
                        ) : (
                          <a href={announcement.document_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                            🎬 {announcement.document_name || 'Watch on Vimeo'}
                          </a>
                        );
                      })()
                    ) : announcement.preview_type === 'youtube' ? (
                      // YouTube - open in embed player modal
                      (() => {
                        const ytMatch = announcement.document_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
                        const ytId = ytMatch ? ytMatch[1] : null;
                        return ytId ? (
                          <button
                            onClick={() => setOpenVideoModals(prev => [...prev, {
                              id: nextModalId(),
                              type: 'youtube',
                              videoId: ytId,
                              title: announcement.document_name
                            }])}
                            className="btn btn-sm btn-outline-primary"
                            style={{ textDecoration: 'none' }}
                          >
                            🎬 {announcement.document_name || 'Watch on YouTube'}
                          </button>
                        ) : (
                          <a href={announcement.document_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                            🎬 {announcement.document_name || 'Watch on YouTube'}
                          </a>
                        );
                      })()
                    ) : (announcement.preview_type === 'pdf' || announcement.document_name?.toLowerCase().endsWith('.pdf')) ? (
                      // PDF - only show button if no thumbnail (thumbnail is already clickable)
                      announcement.preview_url ? null : (
                        <button
                          onClick={() => setOpenPdfModals(prev => [...prev, {
                            id: nextModalId(),
                            url: `${API_BASE_URL}${announcement.document_url}`,
                            title: announcement.document_name
                          }])}
                          className="btn btn-sm btn-outline-primary"
                          style={{ textDecoration: 'none' }}
                        >
                          📄 {announcement.document_name || 'View PDF'}
                        </button>
                      )
                    ) : announcement.document_url.startsWith('/api/') ? (
                      // Internal API URL - use authenticated download
                      <button
                        onClick={() => handleDownload(announcement.document_url, announcement.document_name)}
                        className="btn btn-sm btn-outline-primary"
                        style={{ textDecoration: 'none' }}
                      >
                        📄 {announcement.document_name || 'Download Document'}
                      </button>
                    ) : (
                      // External URL or static file - open in new tab
                      <a
                        href={announcement.document_url.startsWith('/')
                          ? `${API_BASE_URL}${announcement.document_url}`
                          : announcement.document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                        style={{ textDecoration: 'none' }}
                      >
                        📄 {announcement.document_name || 'View Document'}
                      </a>
                    )}
                  </div>
                )}

                {/* View in Resources link */}
                {announcement.resource_location && (
                  <div className="mt-2">
                    <Link
                      to={`/apps/resources?category=${announcement.resource_location.category_id}${announcement.resource_location.folder_id ? `&folder=${announcement.resource_location.folder_id}` : ''}&item=${announcement.resource_location.resource_id}`}
                      className="announcement-resource-link"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px', verticalAlign: 'text-bottom' }}>
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                      </svg>
                      View in Resources
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PDF Preview Modals */}
      {openPdfModals.map(m => (
        <PDFPreviewModal
          key={m.id}
          isOpen={true}
          onClose={() => setOpenPdfModals(prev => prev.filter(p => p.id !== m.id))}
          pdfUrl={m.url}
          title={m.title}
          token={localStorage.getItem('access_token')}
        />
      ))}

      {/* Video Modals (YouTube/Vimeo) */}
      {openVideoModals.map(m => (
        <VideoEmbed
          key={m.id}
          type={m.type}
          videoId={m.videoId}
          title={m.title}
          inModal={true}
          onClose={() => setOpenVideoModals(prev => prev.filter(v => v.id !== m.id))}
        />
      ))}

      {/* Native Video Modals (uploaded videos) */}
      {openNativeVideoModals.map(m => (
        <NativeVideoModal
          key={m.id}
          url={m.url}
          title={m.title}
          onClose={() => setOpenNativeVideoModals(prev => prev.filter(v => v.id !== m.id))}
        />
      ))}
    </div>
  );
};

// Native video player modal for uploaded video files
const NativeVideoModal = ({ url, title, onClose }) => {
  const [videoSrc, setVideoSrc] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchVideoUrl = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const apiUrl = `${url}${url.includes('?') ? '&' : '?'}direct_url=true`;
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) throw new Error('Failed to get video URL');
        const data = await response.json();
        if (!data.url) throw new Error('No URL in response');

        setVideoSrc(data.url);
        setLoading(false);
      } catch (err) {
        console.error('Video load error:', err);
        setError('Failed to load video');
        setLoading(false);
      }
    };
    fetchVideoUrl();
  }, [url]);

  return (
    <ModalShell
      isOpen={true}
      onClose={onClose}
      title={title || 'Video'}
      maxWidth="1200px"
      initialHeight="auto"
      bodyStyle={{ background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}
      headerStyle={{ background: '#1a1a1a', borderBottom: '1px solid #333' }}
      titleStyle={{ color: '#fff' }}
    >
      {loading && (
        <div style={{ textAlign: 'center', color: 'white', padding: '40px' }}>
          <div className="spinner-border text-light" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ marginTop: '12px' }}>Loading video...</p>
        </div>
      )}
      {error && (
        <div style={{ textAlign: 'center', color: 'white', padding: '40px' }}>
          <i className="bi bi-exclamation-triangle" style={{ fontSize: '3rem', color: '#ffc107' }}></i>
          <p style={{ marginTop: '12px' }}>{error}</p>
        </div>
      )}
      {videoSrc && !loading && !error && (
        <video
          controls
          autoPlay
          style={{ maxWidth: '100%', maxHeight: '80vh' }}
          src={videoSrc}
        />
      )}
    </ModalShell>
  );
};

export default Announcements;
